import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { HeroBanner } from '../components/builder/HeroBanner';
import { SectionRenderer } from '../components/builder/SectionRenderer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VenueStoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', event_dates: '', guest_count: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const adminToken = localStorage.getItem('wvc_admin_token');
    axios.get(`${API_URL}/venue-pages/${slug}`, { params: adminToken ? { admin_token: adminToken } : {} })
      .then(res => setPage(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!isLoading && page && window.location.hash === '#calculator') {
      setTimeout(() => {
        document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [isLoading, page]);

  const openEnquiry = useCallback((dates, guests) => {
    setEnquiryForm(prev => ({ ...prev, event_dates: dates || prev.event_dates, guest_count: guests || prev.guest_count }));
    setShowEnquiry(true);
  }, []);

  const submitEnquiry = async () => {
    if (!enquiryForm.name || !enquiryForm.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/venue-pages/${slug}/enquiry`, enquiryForm);
      toast.success('Thank you! The WVC team will call you shortly.');
      setShowEnquiry(false);
      setEnquiryForm({ name: '', phone: '', email: '', event_dates: '', guest_count: '', message: '' });
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#1A3626]/20 border-t-[#1A3626] rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center px-4 text-center font-wvc-body">
        <h1 className="font-wvc-display text-3xl text-[#1A201C]" data-testid="venue-page-not-found">Venue page not found</h1>
        <p className="text-[#748278] mt-2">This venue story may not be published yet.</p>
        <Button onClick={() => navigate('/')} className="mt-6 bg-[#1A3626] hover:bg-[#254834] rounded-full">Back to WVC</Button>
      </div>
    );
  }

  const contact = page.contact || {};
  const sections = (page.sections || []).filter(s => s.visible !== false);

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="venue-story-page">
      <header className="absolute top-0 left-0 right-0 z-30 font-wvc-body">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 text-white" data-testid="story-back-btn">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="flex flex-col items-start leading-none">
              <span className="font-wvc-display text-2xl font-bold tracking-tight">WVC<span className="text-[#C6A87C]">.</span></span>
              <span className="text-[8px] uppercase tracking-[0.28em] text-[#E5D8C6] mt-0.5">A Happy Retreats Company</span>
            </span>
          </button>
          <button
            onClick={() => openEnquiry()}
            className="bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white hover:text-[#1A3626] rounded-full px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors"
            data-testid="header-enquire-btn"
          >
            Talk to WVC Team
          </button>
        </div>
      </header>

      <HeroBanner page={page} />

      {sections.map(section => (
        <SectionRenderer key={section.id} section={section} page={page} onEnquire={openEnquiry} />
      ))}

      <section className="bg-[#F3EFEB] border-t border-[#E5DCCF] py-16 md:py-24 font-wvc-body" data-testid="story-final-cta">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[#C6A87C] uppercase tracking-[0.3em] text-[10px] font-bold">One Honest Conversation</p>
            <h2 className="font-wvc-display text-4xl sm:text-5xl font-light text-[#0A1710] tracking-tight leading-[1.05] mt-4">Ready to plan your celebration at {page.name}?</h2>
            <p className="text-[#1A3626]/70 mt-4 text-sm font-light max-w-lg">Availability, pricing and suitability — all answered honestly by the WVC team.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <button
              onClick={() => openEnquiry()}
              className="bg-[#1A3626] text-[#FDFCF8] hover:bg-[#C6A87C] hover:text-[#0A1710] px-10 py-5 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors"
              data-testid="story-final-cta-btn"
            >
              Talk to WVC Team
            </button>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A1710] text-white py-8 pb-28 md:pb-8 font-wvc-body">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-wvc-display text-lg font-light">{page.name}</p>
          <p className="text-white/40 text-xs font-light">Presented by Wedding Venues Company · A Happy Retreats company</p>
        </div>
      </footer>

      <div className="fixed bottom-0 left-0 right-0 z-40 font-wvc-body" data-testid="sticky-cta-bar">
        <div className="backdrop-blur-xl bg-[#1A3626]/85 border-t border-[#C6A87C]/30">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-10 py-3 flex items-center justify-between gap-3">
            <p className="hidden md:block text-[#FDFCF8]/70 text-xs font-light truncate">{page.name} · Transparent availability, pricing & calculators</p>
            <div className="flex items-center gap-2.5 max-md:w-full max-md:justify-between">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="flex items-center justify-center w-11 h-11 border border-[#C6A87C]/40 text-[#C6A87C] hover:bg-[#C6A87C] hover:text-[#0A1710] transition-colors" data-testid="sticky-call" aria-label="Call WVC">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              {contact.whatsapp && (
                <a href={`https://wa.me/${contact.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center justify-center w-11 h-11 border border-[#C6A87C]/40 text-[#C6A87C] hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors" data-testid="sticky-whatsapp" aria-label="WhatsApp WVC">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                </a>
              )}
              <button
                onClick={() => openEnquiry()}
                className="bg-[#C6A87C] text-[#0A1710] hover:bg-[#FDFCF8] px-7 py-3.5 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors max-md:flex-1"
                data-testid="sticky-enquire-btn"
              >
                Talk to WVC Team
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showEnquiry} onOpenChange={setShowEnquiry}>
        <DialogContent className="sm:max-w-md font-wvc-body">
          <DialogHeader>
            <DialogTitle className="font-wvc-display text-2xl">Talk to the WVC Team</DialogTitle>
            <DialogDescription>Share your details and our team will call you about {page.name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Your name *" value={enquiryForm.name} onChange={e => setEnquiryForm(p => ({ ...p, name: e.target.value }))} data-testid="enquiry-name-input" />
            <Input placeholder="Phone number *" value={enquiryForm.phone} onChange={e => setEnquiryForm(p => ({ ...p, phone: e.target.value }))} data-testid="enquiry-phone-input" />
            <Input placeholder="Email (optional)" type="email" value={enquiryForm.email} onChange={e => setEnquiryForm(p => ({ ...p, email: e.target.value }))} data-testid="enquiry-email-input" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Event dates" value={enquiryForm.event_dates} onChange={e => setEnquiryForm(p => ({ ...p, event_dates: e.target.value }))} data-testid="enquiry-dates-input" />
              <Input placeholder="Guest count" value={enquiryForm.guest_count} onChange={e => setEnquiryForm(p => ({ ...p, guest_count: e.target.value }))} data-testid="enquiry-guests-input" />
            </div>
            <Textarea placeholder="Tell us about your celebration (optional)" value={enquiryForm.message} onChange={e => setEnquiryForm(p => ({ ...p, message: e.target.value }))} data-testid="enquiry-message-input" />
            <Button onClick={submitEnquiry} disabled={isSubmitting} className="w-full bg-[#1A3626] hover:bg-[#254834] rounded-full" data-testid="enquiry-submit-btn">
              {isSubmitting ? 'Sending...' : 'Talk to WVC Team'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
